import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import api from '../../../lib/api';

export default function DietPlanManager() {
  const [plans, setPlans] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [view, setView] = useState('list');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [assigningPlanId, setAssigningPlanId] = useState(null);
  const [assignMemberId, setAssignMemberId] = useState('');
  const [newPlan, setNewPlan] = useState({
    title: '',
    member_id: '',
    meals: [
      { time: 'Breakfast', meal: '', calories: 0, protein: 0, carbs: 0, fat: 0 },
    ],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [plansRes, membersRes] = await Promise.all([
        api.get('/diet-plans'),
        api.get('/members', { params: { status: 'active', limit: 2000 } }),
      ]);
      setPlans(plansRes?.data || []);
      setMembers(membersRes?.data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addMeal = () => {
    setNewPlan(p => ({
      ...p,
      meals: [...p?.meals, { time: '', meal: '', calories: 0, protein: 0, carbs: 0, fat: 0 }],
    }));
  };

  const removeMeal = (idx) => {
    setNewPlan(p => ({ ...p, meals: p?.meals?.filter((_, i) => i !== idx) }));
  };

  const updateMeal = (idx, field, value) => {
    setNewPlan(p => ({
      ...p,
      meals: p?.meals?.map((m, i) => i === idx ? { ...m, [field]: field === 'meal' || field === 'time' ? value : Number(value) } : m),
    }));
  };

  const totalCalories = newPlan?.meals?.reduce((s, m) => s + (Number(m?.calories) || 0), 0);
  const totalProtein = newPlan?.meals?.reduce((s, m) => s + (Number(m?.protein) || 0), 0);
  const totalCarbs = newPlan?.meals?.reduce((s, m) => s + (Number(m?.carbs) || 0), 0);
  const totalFat = newPlan?.meals?.reduce((s, m) => s + (Number(m?.fat) || 0), 0);
  const totalMacros = totalProtein + totalCarbs + totalFat;
  const macroPercent = (val) => totalMacros > 0 ? Math.round((val / totalMacros) * 100) : 0;

  const handleCreatePlan = async () => {
    if (!newPlan?.title?.trim()) { setError('Plan title is required'); return; }
    setSaving(true);
    setError(null);
    try {
      await api.post('/diet-plans', {
        member_id: newPlan?.member_id || null,
        title: newPlan?.title?.trim(),
        meals: newPlan?.meals,
        total_calories: totalCalories,
      });
      setSuccess('Diet plan created successfully!');
      setTimeout(() => setSuccess(null), 3000);
      setNewPlan({ title: '', member_id: '', meals: [{ time: 'Breakfast', meal: '', calories: 0, protein: 0, carbs: 0, fat: 0 }] });
      setView('list');
      fetchData();
    } catch (err) {
      setError(err?.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPlan = async (planId, memberId) => {
    if (!memberId) return;
    setAssigningPlanId(planId);
    try {
      await api.put(`/diet-plans/${planId}`, { member_id: memberId });
      setSuccess('Diet plan assigned successfully!');
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err) {
      setError(err?.message);
    } finally {
      setAssigningPlanId(null);
      setAssignMemberId('');
    }
  };

  const getPlanMacros = (meals) => {
    if (!meals?.length) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    return meals?.reduce((acc, m) => ({
      calories: acc?.calories + (Number(m?.calories) || 0),
      protein: acc?.protein + (Number(m?.protein) || 0),
      carbs: acc?.carbs + (Number(m?.carbs) || 0),
      fat: acc?.fat + (Number(m?.fat) || 0),
    }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
  };

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] p-5 shadow-[var(--shadow-sm)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--color-foreground)] font-[var(--font-heading)]">Diet Plan Manager</h3>
        <button
          onClick={() => setView(view === 'list' ? 'create' : 'list')}
          className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-accent)] text-white rounded-[var(--radius-sm)] text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
        >
          <Icon name={view === 'list' ? 'Plus' : 'List'} size={13} color="#fff" />
          {view === 'list' ? 'New Plan' : 'View Plans'}
        </button>
      </div>
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-[var(--radius-sm)]">
          <Icon name="AlertCircle" size={16} color="var(--color-error)" />
          <p className="text-sm text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-[var(--color-success)]/10 border border-[var(--color-success)]/20 rounded-[var(--radius-sm)]">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          <p className="text-sm text-[var(--color-success)] font-[var(--font-caption)]">{success}</p>
        </div>
      )}
      {view === 'list' ? (
        loading ? (
          <div className="flex flex-col gap-3">
            {[1, 2, 3]?.map(i => <div key={i} className="h-16 bg-[var(--color-muted)] rounded animate-pulse" />)}
          </div>
        ) : plans?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Icon name="UtensilsCrossed" size={32} color="var(--color-muted-foreground)" />
            <p className="mt-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">No diet plans yet</p>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">Click "New Plan" to create one</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {plans?.map((plan) => {
              const macros = getPlanMacros(plan?.meals);
              return (
                <div key={plan?.id} className="p-4 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] hover:border-[var(--color-accent)]/30 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{plan?.title}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <span className="text-xs font-bold text-[var(--color-accent)] font-[var(--font-caption)]">{macros?.calories} kcal</span>
                        <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{macros?.protein}P · {macros?.carbs}C · {macros?.fat}F</span>
                        {plan?.member_name && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--color-accent)]/10 text-[var(--color-accent)] font-medium font-[var(--font-caption)]">
                            {plan?.member_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedPlan(selectedPlan?.id === plan?.id ? null : plan)}
                      className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors flex-shrink-0"
                    >
                      <Icon name={selectedPlan?.id === plan?.id ? 'ChevronUp' : 'ChevronDown'} size={14} color="var(--color-muted-foreground)" />
                    </button>
                  </div>
                  {selectedPlan?.id === plan?.id && (
                    <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                      {plan?.meals?.map((meal, mi) => (
                        <div key={mi} className="flex items-center gap-3 py-2 border-b border-[var(--color-border)] last:border-0">
                          <div className="w-7 h-7 rounded bg-[var(--color-accent)]/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="UtensilsCrossed" size={12} color="var(--color-accent)" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold text-[var(--color-primary)] font-[var(--font-caption)]">{meal?.time}</span>
                            <p className="text-xs text-[var(--color-foreground)] font-[var(--font-caption)] truncate">{meal?.meal}</p>
                          </div>
                          <span className="text-xs font-bold text-[var(--color-foreground)] font-[var(--font-caption)] flex-shrink-0">{meal?.calories} kcal</span>
                        </div>
                      ))}
                      {/* Assign to member */}
                      <div className="flex items-center gap-2 mt-3">
                        <select
                          value={assignMemberId}
                          onChange={e => setAssignMemberId(e?.target?.value)}
                          className="flex-1 px-2 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                        >
                          <option value="">Assign to member...</option>
                          {members?.map(m => <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>)}
                        </select>
                        <button
                          onClick={() => handleAssignPlan(plan?.id, assignMemberId)}
                          disabled={!assignMemberId || assigningPlanId === plan?.id}
                          className="px-3 py-1.5 bg-[var(--color-accent)] text-white rounded text-xs font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                          {assigningPlanId === plan?.id ? 'Assigning...' : 'Assign'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Create Plan Form */
        (<div className="flex flex-col gap-4">
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Plan Title *</label>
            <input
              type="text"
              value={newPlan?.title}
              onChange={e => setNewPlan(p => ({ ...p, title: e?.target?.value }))}
              placeholder="e.g. High Protein Diet Plan"
              className="w-full px-3 py-2.5 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-1.5">Assign to Member</label>
            <select
              value={newPlan?.member_id}
              onChange={e => setNewPlan(p => ({ ...p, member_id: e?.target?.value }))}
              className="w-full px-3 py-2 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] text-sm text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
            >
              <option value="">Select member (optional)</option>
              {members?.map(m => <option key={m?.id} value={m?.id}>{m?.first_name} {m?.last_name}</option>)}
            </select>
          </div>
          {/* Macro Summary */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Calories', value: totalCalories, unit: 'kcal', color: 'var(--color-accent)' },
              { label: 'Protein', value: `${totalProtein}g`, unit: `${macroPercent(totalProtein)}%`, color: 'var(--color-primary)' },
              { label: 'Carbs', value: `${totalCarbs}g`, unit: `${macroPercent(totalCarbs)}%`, color: 'var(--color-secondary)' },
              { label: 'Fat', value: `${totalFat}g`, unit: `${macroPercent(totalFat)}%`, color: 'var(--color-warning)' },
            ]?.map((macro) => (
              <div key={macro?.label} className="bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)] p-2 text-center">
                <p className="text-base font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">{macro?.value}</p>
                <p className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{macro?.label}</p>
                <p className="text-[10px] font-medium font-[var(--font-caption)]" style={{ color: macro?.color }}>{macro?.unit}</p>
              </div>
            ))}
          </div>
          {/* Meals */}
          <div className="flex flex-col gap-3">
            {newPlan?.meals?.map((meal, idx) => (
              <div key={idx} className="p-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-[var(--radius-sm)]">
                <div className="flex items-center gap-2 mb-2">
                  <input
                    type="text"
                    value={meal?.time}
                    onChange={e => updateMeal(idx, 'time', e?.target?.value)}
                    placeholder="Meal time (e.g. Breakfast)"
                    className="flex-1 px-2 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)]"
                  />
                  {newPlan?.meals?.length > 1 && (
                    <button onClick={() => removeMeal(idx)} className="p-1 rounded hover:bg-[var(--color-error)]/10 transition-colors">
                      <Icon name="Trash2" size={13} color="var(--color-error)" />
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={meal?.meal}
                  onChange={e => updateMeal(idx, 'meal', e?.target?.value)}
                  placeholder="Meal description"
                  className="w-full px-2 py-1.5 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] focus:outline-none focus:border-[var(--color-primary)] mb-2"
                />
                <div className="grid grid-cols-4 gap-2">
                  {['calories', 'protein', 'carbs', 'fat']?.map(field => (
                    <label key={field} className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-[var(--color-muted-foreground)] font-[var(--font-caption)] capitalize">{field}</span>
                      <input
                        type="number"
                        value={meal?.[field]}
                        onChange={e => updateMeal(idx, field, e?.target?.value)}
                        className="px-2 py-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded text-xs text-[var(--color-foreground)] font-[var(--font-caption)] text-center focus:outline-none focus:border-[var(--color-primary)]"
                      />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={addMeal}
            className="flex items-center gap-2 px-3 py-2 border border-dashed border-[var(--color-accent)]/40 rounded-[var(--radius-sm)] text-xs font-medium text-[var(--color-accent)] font-[var(--font-caption)] hover:bg-[var(--color-accent)]/5 transition-colors"
          >
            <Icon name="Plus" size={13} color="var(--color-accent)" />
            Add Meal
          </button>
          <button
            onClick={handleCreatePlan}
            disabled={saving || !newPlan?.title?.trim()}
            className="flex items-center justify-center gap-2 py-2.5 bg-[var(--color-accent)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <Icon name="Loader2" size={14} color="#fff" className="animate-spin" /> : <Icon name="Save" size={14} color="#fff" />}
            {saving ? 'Saving...' : 'Save Diet Plan'}
          </button>
        </div>)
      )}
    </div>
  );
}
